import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { CalendarDays, Clock, MapPin, ArrowRight, Sparkles, Share2, Check } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import ActiviteitenPartners from "../components/ActiviteitenPartners";

const CATEGORIES = [
  { id: "uitgelicht", label: "Uitgelicht" },
  { id: "all", label: "Alles" },
  { id: "strandbar", label: "Strandbar" },
  { id: "kids", label: "Kinderstrand" },
  { id: "sport", label: "Sport" },
  { id: "volleybal", label: "Beachvolleybal" },
  { id: "party", label: "Party" },
  { id: "muziek", label: "Muziek" },
  { id: "activiteiten", label: "Activiteiten" },
];

const ACTIVITEITEN_CATS = new Set(["kids", "sport"]);

const CAT_META = {
  strandbar: { label: "Strandbar", tone: "bg-[#fde3c5] text-[#a8470a]" },
  kids: { label: "Kinderstrand", tone: "bg-[#cfe9f5] text-[#1f6e8c]" },
  sport: { label: "Sport", tone: "bg-[#d8edd2] text-[#3a6c2f]" },
  volleybal: { label: "Beachvolleybal", tone: "bg-[#fdd9b1] text-[#a8470a]" },
  party: { label: "Party", tone: "bg-[#f1d3e8] text-[#8b2462]" },
  muziek: { label: "Muziek", tone: "bg-[#e4dcf5] text-[#4a2a8a]" },
};

const EVENT_YEAR = 2026;
const NL_WEEKDAYS = ["ZO", "MA", "DI", "WO", "DO", "VR", "ZA"];

const VIBRATIONZ_POSTER =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/pkfvia6m_Vibrationz.png";

const BON_MARCHE_POSTER =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/ggqg1r1w_its.bon.marche.jpg";

const DARTS_POSTER =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/e8jnqtus_visual%20darts.jpeg";

const SPEEDDATES_POSTER =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/khs6kw98_speeddates%20vert.jpeg";

const BEACHGAMES_POSTER =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/p7vc0fw1_beachgames%20vert.jpeg";

const SMACK_THAT_VISUAL =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/qr0v4j04_visual%20smack%20that%20%282%29.png";

const LOBEL_PHOTO =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/lgecwgex_Lobel%20%281%29.jpeg";

const RADIO_BOTANIQUE_PHOTO =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/ot08ag5n_radio%20botanique.jpg";

const ROUNDNET_11_PHOTO =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/ik8pvc8q_roundnet%2011.jpg";

const KIDS_OPENING_PHOTO =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/ahv63l1e_kinderstrand%20-%20boogschieten.jpg";

const KIDS_KNUTSEL_PHOTO =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/busmfpzt_knutselen.jpg";

// DJ photos — consistent met Line-up
const DJ_DYSFUNKSHUNAL_PHOTO =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/mmqpent1_DJ%20Dysfunkshunal%20press%20pic%2010.jpg";
const DJ_KURT_PHOTO =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/dd1ww1so_Dj%20Kurt%20Verheyen.jpg";
const DJ_SHANE_D_PHOTO =
  "https://customer-assets-jt897jd0.emergentagent.net/job_mechelen-summer/artifacts/2lh37vl8_Shane-D%20%282%29.jpg";
const DJ_KEZZE_PHOTO =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/aqc0mg5p_Kezze.png";
const DJ_SMASHED_PHOTO =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/mtj5ru1x_Smashed%20Dj%27s.jpeg";

const LATIN_BEACH_NIGHTS_VISUAL =
  "https://customer-assets-jt897jd0.emergentagent.net/job_mechelen-summer/artifacts/i8ay0y2b_ChatGPT%20Image%2014%20jul%202026%2C%2001_46_24.png";
const SCHAKEN_PHOTO =
  "https://customer-assets-jt897jd0.emergentagent.net/job_mechelen-summer/artifacts/x0t896to_Schaken.webp";
const KABOUTERWANDELING_PHOTO =
  "https://customer-assets-jt897jd0.emergentagent.net/job_mechelen-summer/artifacts/m2n0kkjg_kabouterwandeling.webp";

const PROGRAM = [
  // General / daily
  {
    cat: "strandbar",
    title: "Strandbar",
    date: "16/7 t.e.m. 9/8",
    time: "Dagelijks vanaf 13u",
    description:
      "Geniet van zomerse sfeer, muziek, drankjes, foodtrucks en gezellige momenten. Elke dag vanaf 13u.",
    location: "Douaneplein / Transit M",
    highlight: false,
  },
  {
    cat: "kids",
    title: "Kinderactiviteiten — ganse periode",
    date: "20/7 t.e.m. 9/8",
    time: "Dagelijks vanaf 13u",
    description:
      "Springkastelen, speelstrand, spelmateriaal, drankjes en foodtrucks. Elke dag vanaf 13u.",
    location: "Douaneplein / Transit M",
  },
  {
    cat: "sport",
    title: "Vrij sport en spel — ganse periode",
    date: "20/7 t.e.m. 9/8",
    time: "Dagelijks vanaf 13u",
    description:
      "Beachvolley, Kubb, Roundnet, skate, badminton, etc. Kom spelen op ons sportstrand en ontleen gerust sportmateriaal uit onze uitleendienst. Elke dag vanaf 13u.",
    location: "Douaneplein / Transit M",
  },

  // Strandbar / afterwork / salsa
  {
    cat: "strandbar",
    title: "Music Afterwork — Lobel & Mr Prosecco",
    date: "22/07",
    time: "18u – 00u",
    description:
      "Leuke afterwork op woensdag om gezellig iets te drinken en nadien een dansje te placeren met Lobel en Mr Prosecco.",
    location: "Douaneplein / Transit M",
    extraCats: ["party"],
    thumbnail: LOBEL_PHOTO,
    thumbnailFit: "contain",
    highlight: true,
  },
  {
    cat: "strandbar",
    title: "Music Afterwork — SmacK That",
    date: "29/07",
    time: "18u – 00u",
    description:
      "Throwback R&B & Hiphop afterwork op woensdag met SmacK That. Live line-up vanaf 18u.",
    paragraphs: [
      "18u – 19u30: O'Jay",
      "19u30 – 21u: Tjua Lotus",
      "21u – 22u30: Pau Prince",
      "22u30 – 00u: Tjua Lotus",
    ],
    location: "Douaneplein / Transit M",
    extraCats: ["party"],
    visual: SMACK_THAT_VISUAL,
    visualBg: "white",
    button: {
      label: "Bekijk op Instagram",
      href: "https://www.instagram.com/smack_that.be/",
    },
    highlight: true,
  },
  {
    cat: "strandbar",
    title: "Music Afterwork — Radio Botanique",
    date: "05/08",
    time: "18u – 00u",
    description:
      "Leuke afterwork op woensdag om gezellig iets te drinken en nadien een dansje te placeren met Radio Botanique.",
    location: "Douaneplein / Transit M",
    extraCats: ["party"],
    thumbnail: RADIO_BOTANIQUE_PHOTO,
    thumbnailFit: "contain",
    highlight: true,
  },
  {
    cat: "strandbar",
    title: "Latin beach nights op maandagen en donderdagen",
    slug: "latin-beach-nights",
    date: "20/7, 23/7, 27/7, 30/7, 3/8, 6/8",
    dates: [
      { startDate: "2026-07-20", endDate: "2026-07-20", startTime: "19:00", endTime: "23:59" },
      { startDate: "2026-07-23", endDate: "2026-07-23", startTime: "19:00", endTime: "23:59" },
      { startDate: "2026-07-27", endDate: "2026-07-27", startTime: "19:00", endTime: "23:59" },
      { startDate: "2026-07-30", endDate: "2026-07-30", startTime: "19:00", endTime: "23:59" },
      { startDate: "2026-08-03", endDate: "2026-08-03", startTime: "19:00", endTime: "23:59" },
      { startDate: "2026-08-06", endDate: "2026-08-06", startTime: "19:00", endTime: "23:59" },
    ],
    time: "Vanaf 19u",
    description:
      "Kom salsa en Bachata dansen op maandagen en donderdagen. Initiaties en vrij dansen met Dj El Capitan.",
    paragraphs: [
      "Kom salsa en Bachata dansen op maandagen en donderdagen!",
      "Maandagen = salsamuziek",
      "19u: gratis initiatie salsa voor absolute beginners.",
      "19u30: gratis initiatie salsa voor alle niveaus, met extra aandacht voor de meer gevorderde dansers.",
      "20u – 00u: Dj El Capitan",
      "Donderdagen = bachata- en kizombamuziek, aangevuld met wat salsa en eventueel discoswing",
      "19u: gratis initiatie Bachata (23/7) / Kizomba (30/7) voor absolute beginners.",
      "19u30: gratis initiatie Bachata (23/7) / Kizomba (30/7) voor alle niveaus, met extra aandacht voor de meer gevorderde dansers.",
      "20u – 00u: Dj El Capitan",
    ],
    location: "Tent",
    thumbnail: LATIN_BEACH_NIGHTS_VISUAL,
    thumbnailFit: "contain",
    highlight: true,
    uitgelichtOnly: true,
  },
  {
    cat: "strandbar",
    title: "Latin beach night : Salsa",
    slug: "latin-beach-night-salsa-20-07",
    date: "20/07",
    startDate: "2026-07-20",
    endDate: "2026-07-20",
    startTime: "19:00",
    endTime: "23:59",
    time: "Vanaf 19u",
    description: "Latin beach night met salsamuziek en Dj El Capitan.",
    paragraphs: [
      "19u: gratis initiatie salsa voor absolute beginners.",
      "19u30: gratis initiatie salsa voor alle niveaus, met extra aandacht voor de meer gevorderde dansers.",
      "20u – 00u: Dj El Capitan",
    ],
    location: "Tent",
    thumbnail: LATIN_BEACH_NIGHTS_VISUAL,
    thumbnailFit: "contain",
  },
  {
    cat: "strandbar",
    title: "Latin beach night : bachata en kizomba",
    slug: "latin-beach-night-bachata-23-07",
    date: "23/07",
    startDate: "2026-07-23",
    endDate: "2026-07-23",
    startTime: "19:00",
    endTime: "23:59",
    time: "Vanaf 19u",
    description: "Latin beach night met bachata- en kizombamuziek en Dj El Capitan.",
    paragraphs: [
      "19u: gratis initiatie Bachata (23/7) / Kizomba (30/7) voor absolute beginners.",
      "19u30: gratis initiatie Bachata (23/7) / Kizomba (30/7) voor alle niveaus, met extra aandacht voor de meer gevorderde dansers.",
      "20u – 00u: Dj El Capitan",
    ],
    location: "Tent",
    thumbnail: LATIN_BEACH_NIGHTS_VISUAL,
    thumbnailFit: "contain",
  },
  {
    cat: "strandbar",
    title: "Latin beach night : Salsa",
    slug: "latin-beach-night-salsa-27-07",
    date: "27/07",
    startDate: "2026-07-27",
    endDate: "2026-07-27",
    startTime: "19:00",
    endTime: "23:59",
    time: "Vanaf 19u",
    description: "Latin beach night met salsamuziek en Dj El Capitan.",
    paragraphs: [
      "19u: gratis initiatie salsa voor absolute beginners.",
      "19u30: gratis initiatie salsa voor alle niveaus, met extra aandacht voor de meer gevorderde dansers.",
      "20u – 00u: Dj El Capitan",
    ],
    location: "Tent",
    thumbnail: LATIN_BEACH_NIGHTS_VISUAL,
    thumbnailFit: "contain",
  },
  {
    cat: "strandbar",
    title: "Latin beach night : bachata en kizomba",
    slug: "latin-beach-night-bachata-30-07",
    date: "30/07",
    startDate: "2026-07-30",
    endDate: "2026-07-30",
    startTime: "19:00",
    endTime: "23:59",
    time: "Vanaf 19u",
    description: "Latin beach night met bachata- en kizombamuziek en Dj El Capitan.",
    paragraphs: [
      "19u: gratis initiatie Bachata (23/7) / Kizomba (30/7) voor absolute beginners.",
      "19u30: gratis initiatie Bachata (23/7) / Kizomba (30/7) voor alle niveaus, met extra aandacht voor de meer gevorderde dansers.",
      "20u – 00u: Dj El Capitan",
    ],
    location: "Tent",
    thumbnail: LATIN_BEACH_NIGHTS_VISUAL,
    thumbnailFit: "contain",
  },
  {
    cat: "strandbar",
    title: "Latin beach night : Salsa",
    slug: "latin-beach-night-salsa-03-08",
    date: "03/08",
    startDate: "2026-08-03",
    endDate: "2026-08-03",
    startTime: "19:00",
    endTime: "23:59",
    time: "Vanaf 19u",
    description: "Latin beach night met salsamuziek en Dj El Capitan.",
    paragraphs: [
      "19u: gratis initiatie salsa voor absolute beginners.",
      "19u30: gratis initiatie salsa voor alle niveaus, met extra aandacht voor de meer gevorderde dansers.",
      "20u – 00u: Dj El Capitan",
    ],
    location: "Tent",
    thumbnail: LATIN_BEACH_NIGHTS_VISUAL,
    thumbnailFit: "contain",
  },
  {
    cat: "strandbar",
    title: "Latin beach night : bachata en kizomba",
    slug: "latin-beach-night-bachata-06-08",
    date: "06/08",
    startDate: "2026-08-06",
    endDate: "2026-08-06",
    startTime: "19:00",
    endTime: "23:59",
    time: "Vanaf 19u",
    description: "Latin beach night met bachata- en kizombamuziek en Dj El Capitan.",
    paragraphs: [
      "19u: gratis initiatie Bachata (23/7) / Kizomba (30/7) voor absolute beginners.",
      "19u30: gratis initiatie Bachata (23/7) / Kizomba (30/7) voor alle niveaus, met extra aandacht voor de meer gevorderde dansers.",
      "20u – 00u: Dj El Capitan",
    ],
    location: "Tent",
    thumbnail: LATIN_BEACH_NIGHTS_VISUAL,
    thumbnailFit: "contain",
  },
  {
    cat: "strandbar",
    title: "Marktje Bon Marché",
    date: "25/07",
    time: "13u – 18u",
    description: "Een zomerse makersmarkt tijdens Mechelen Beach in Mechelen.",
    paragraphs: [
      "Een zomerse makersmarkt tijdens Mechelen Beach in Mechelen 🌿✨",
      "Een dag vol creatieve ondernemers, unieke vondsten en zalige summer vibes!",
      "Van 13u tot 18u:",
      "🛍️ Shop bij onze creatieve standhouders en ontdek juwelen, illustraties, kaartjes, keramiek, handgemaakte goodies, kinder- en vrouwenkleding, kinderspeelgoed, accessoires, lekkers en nog véél meer moois",
      "🎨 Voor de kleinsten is er ook kindergrime voorzien, zodat ze zich even helemaal kunnen laten omtoveren ✨",
      "☀️ Slenter rustig rond, laat je verrassen en geniet van de gezellige festivalsfeer van Mechelen Beach",
      "🍹 Bestel iets fris, plof neer in de zon en maak er gewoon een heerlijke zomerdag van met vrienden of familie",
      "Kom langs, breng je favoriete gezelschap mee en geniet van een dag vol mooie dingen, fijne vibes en zomergevoel 💫",
      "Zien we jou daar?",
      "Volg @its.bon.marche op Instagram als je niets wil missen ✨",
    ],
    button: {
      label: "Bekijk op Instagram",
      href: "https://www.instagram.com/its.bon.marche/",
    },
    visual: BON_MARCHE_POSTER,
    visualBg: "white",
  },

  // Beachvolleybal
  {
    cat: "volleybal",
    title: "Bedrijventornooi Beachvolley 6 vs 6",
    date: "16/07",
    time: "17u – 23u",
    description:
      "26e editie van het Mechelen Beach volleybaltornooi (volzet). Meer info op de volleypagina.",
    location: "Strand",
    button: { label: "Bekijk beachvolleybal", to: "/beachvolleybal" },
    highlight: false,
  },
  {
    cat: "volleybal",
    title: "Bedrijventornooi Beachvolley 6 vs 6",
    date: "17/07",
    time: "17u – 23u",
    description:
      "26e editie van het Mechelen Beach volleybaltornooi. Meer info op de volleypagina.",
    location: "Strand",
    button: { label: "Bekijk beachvolleybal", to: "/beachvolleybal" },
    highlight: true,
  },
  {
    cat: "volleybal",
    title: "Recreatief tornooi Beachvolley 6 vs 6",
    date: "18/07",
    time: "13u – 21u",
    description:
      "26e editie van het Mechelen Beach volleybaltornooi. Meer info op de volleypagina.",
    location: "Strand",
    button: { label: "Bekijk beachvolleybal", to: "/beachvolleybal" },
    highlight: true,
  },
  {
    cat: "volleybal",
    title: "Recreatief tornooi Beachvolley 6 vs 6",
    date: "19/07",
    time: "13u – 21u",
    description:
      "26e editie van het Mechelen Beach volleybaltornooi. Meer info op de volleypagina.",
    location: "Strand",
    button: { label: "Bekijk beachvolleybal", to: "/beachvolleybal" },
    highlight: true,
  },

  // Sport
  {
    cat: "sport",
    title: "Beachgames & Party",
    date: "24/07",
    time: "Vanaf 18u",
    description:
      "Beachgames & Party op Mechelen Beach in samenwerking met Single Events 2800. Doe mee met de beach games en sluit nadien aan op de party.",
    button: {
      label: "Schrijf je in",
      href: "https://billetto.be/e/beach-games-party-Tickets-1959620?utm_source=organiser&utm_medium=share&utm_campaign=copy_link&utm_content=2",
    },
    visual: BEACHGAMES_POSTER,
    visualBg: "white",
    location: "Strand",
  },
  {
    cat: "sport",
    title: "Roundnet Tornooi",
    date: "01/08",
    description:
      "Spikeball tornooi in samenwerking met Roundnet Mechelen.",
    location: "Strand",
    thumbnail: "https://customer-assets-jt897jd0.emergentagent.net/job_mechelen-summer/artifacts/89ms6r6y_roundnet_2_resized.jpg",
    buttons: [
      { label: "Meer info", href: "https://fwango.io/rmst26" },
      { label: "Inschrijven", href: "https://tally.so/r/zxQr5a", primary: true },
    ],
  },
  {
    cat: "sport",
    title: "Social run — RAM",
    date: "07/08",
    time: "18u30",
    description:
      "Social run van 5 km, 8 km of 12 km in samenwerking met Regionale atletiekverenigingen Mechelen.",
    location: "Strand",
    button: {
      label: "Meer info en inschrijven",
      href: "https://ram-atletiek.be/events/rammechelen-beach/",
    },
  },
  {
    cat: "sport",
    title: "Social run — Runners High Collective",
    date: "23/7 en 5/8",
    dates: [
      { startDate: "2026-07-23", endDate: "2026-07-23", startTime: "19:00", endTime: "21:00" },
      { startDate: "2026-08-05", endDate: "2026-08-05", startTime: "19:00", endTime: "21:00" },
    ],
    time: "19u",
    description:
      "Social run met de sympathieke bende van Runners High Collective.",
    location: "Strand",
  },
  {
    cat: "sport",
    title: "Capoeira initiatie",
    date: "01/08",
    time: "18u",
    description: "Capoeira workshop & demo.",
    location: "Tent",
    thumbnail: "https://customer-assets-jt897jd0.emergentagent.net/job_mechelen-summer/artifacts/907tzsph_capoeira_25_3_resized.jpg",
  },
  {
    cat: "sport",
    title: "Schaaktornooi — Blitztornooi",
    date: "28/07",
    time: "Aanmelden vanaf 19u30 · start 20u",
    description:
      "Blitztornooi in samenwerking met KMSK Mechelen. Aanmelden vanaf 19u30, tot max. 40 deelnemers, start om 20u.",
    location: "Strand",
    thumbnail: SCHAKEN_PHOTO,
  },
  {
    cat: "sport",
    title: "Schaaktornooi — Simultaan",
    date: "04/08",
    time: "Aanmelden vanaf 19u30 · start 20u",
    description:
      "Simultaan in samenwerking met KMSK Mechelen. Aanmelden vanaf 19u30, tot max. 20 deelnemers, start om 20u.",
    location: "Strand",
    thumbnail: SCHAKEN_PHOTO,
  },
  {
    cat: "sport",
    title: "Schaak initiatie",
    date: "26/7 & 2/8",
    dates: [
      { startDate: "2026-07-26", endDate: "2026-07-26", startTime: "14:00", endTime: "17:30" },
      { startDate: "2026-08-02", endDate: "2026-08-02", startTime: "14:00", endTime: "17:30" },
    ],
    time: "Vanaf 14u",
    description: "Schaakinitiaties voor de kinderen op de kindernamiddag.",
    location: "Strand",
    thumbnail: "https://customer-assets-jt897jd0.emergentagent.net/job_mechelen-summer/artifacts/5x4go618_schaak%20initiatie.png",
    thumbnailFit: "contain",
  },
  {
    cat: "sport",
    title: "Airbadminton tornooi",
    date: "02/08",
    time: "Initiaties vanaf 13u30 · Tornooi vanaf 16u30",
    description:
      "Airbadminton tornooi in samenwerking met Badminton Vlaanderen. Initiaties starten om 13u30, tornooi vanaf 16u30.",
    location: "Strand",
    button: {
      label: "Schrijf je in",
      href: "https://static.twizzit.com/v2/form/b01OdFlXNHRXWXFQRkZsNUtMZW1Rdz09",
    },
    visual: "https://customer-assets-jt897jd0.emergentagent.net/job_mechelen-summer/artifacts/kx01z34q_Airbatminton.png",
    visualBg: "white",
  },
  {
    cat: "sport",
    title: "Roundnet open trainingen",
    date: "23/07, 30/07 en 06/08",
    dates: [
      { startDate: "2026-07-23", endDate: "2026-07-23", startTime: "19:00", endTime: "21:00" },
      { startDate: "2026-07-30", endDate: "2026-07-30", startTime: "19:00", endTime: "21:00" },
      { startDate: "2026-08-06", endDate: "2026-08-06", startTime: "19:00", endTime: "21:00" },
    ],
    time: "19u – 21u",
    description:
      "3 donderdagen is het open Roundnet training (spikeball) op Mechelen Beach. Op 23/07, 30/07 en 06/08 van 19u-21u. Nieuwe spelers zijn altijd welkom, geen ervaring nodig.",
    location: "Strand",
    thumbnail: ROUNDNET_11_PHOTO,
    button: {
      label: "Schrijf je in",
      href: "https://tally.so/r/Bz6A8Y",
    },
  },
  {
    cat: "sport",
    title: "Darts tornooi",
    date: "08/08",
    time: "Vanaf 13u",
    description:
      "Darts tornooi op Mechelen Beach. Schrijf je in en gooi mee voor de overwinning.",
    location: "Strand",
    visual: DARTS_POSTER,
  },
  {
    cat: "sport",
    title: "Gratis groepslessen met I.Shape",
    date: "08/08",
    time: "Start 14u, 14u45, 15u30",
    description:
      "3x 30 min ISHAPE groepsles — mix van HITT IT, SHAPE POWER en BARRE FLOW.",
    paragraphs: [
      "Start 14u, 14u45, 15u30",
      "3x 30 min ISHAPE groepsles (mix van drie lessen namelijk: HITT IT, SHAPE POWER en BARRE FLOW)",
      "HITT IT combineert cardio en explosiviteit en een hoge calorie burn. Het bestaat uit martial arts bewegingen om je fitter en sterker te maken.",
      "SHAPE POWER traint spieruithoudingsvermogen door bewegingen uit krachttraining te gebruiken met een hoge time under tension op toffe muziek!",
      "BARRE FLOW: Door middel van yoga en pilates geïnspireerde bewegingen verbeter je krachtuithouding en train je flexibiliteit.",
    ],
    location: "Strand",
    button: {
      label: "Schrijf je in",
      href: "https://www.eventbrite.be/e/mechelen-beach-x-ishape-tickets-1993145800744?aff=oddtdtcreator&keep_tld=true",
    },
  },
  {
    cat: "strandbar",
    title: "Sunset speeddates",
    date: "21/07",
    description:
      "Sunset speeddates op Mechelen Beach in samenwerking met Single Events 2800. Maak nieuwe connecties bij zonsondergang.",
    button: {
      label: "Schrijf je in",
      href: "https://billetto.be/e/sunset-speeddates-party-mechelen-beach-Tickets-1959619?utm_source=organiser&utm_medium=share&utm_campaign=copy_link&utm_content=2",
    },
    visual: SPEEDDATES_POSTER,
    visualBg: "white",
    location: "Strand",
  },

  // Kinderstrand
  {
    cat: "kids",
    title: "Opening kinderstrand",
    date: "21/07",
    time: "13u – 17u30",
    description:
      "Feestelijke opening kinderstrand in samenwerking met Jeti VZW. Crea, grime, boogschieten en knutselworkshop.",
    location: "Kinderstrand",
    thumbnail: KIDS_OPENING_PHOTO,
    highlight: true,
  },
  {
    cat: "kids",
    title: "Kindernamiddagen met extra activiteiten",
    date: "21/7, 22/7, 26/7, 29/7, 2/8, 5/8, 9/8",
    dates: [
      { startDate: "2026-07-21", endDate: "2026-07-21", startTime: "13:00", endTime: "17:30" },
      { startDate: "2026-07-22", endDate: "2026-07-22", startTime: "13:00", endTime: "17:30" },
      { startDate: "2026-07-26", endDate: "2026-07-26", startTime: "13:00", endTime: "17:30" },
      { startDate: "2026-07-29", endDate: "2026-07-29", startTime: "13:00", endTime: "17:30" },
      { startDate: "2026-08-02", endDate: "2026-08-02", startTime: "13:00", endTime: "17:30" },
      { startDate: "2026-08-05", endDate: "2026-08-05", startTime: "13:00", endTime: "17:30" },
      { startDate: "2026-08-09", endDate: "2026-08-09", startTime: "13:00", endTime: "17:30" },
    ],
    time: "13u – 17u30",
    description:
      "Op woensdag- en zondagnamiddagen organiseren we extra kinderactiviteiten in samenwerking met het Speelgoedmuseum, ballonnenclown Ivan, Cirkolito, Schaakclub Mechelen en Jeti VZW.",
    location: "Kinderstrand",
    thumbnail: KIDS_KNUTSEL_PHOTO,
    highlight: true,
  },
  {
    cat: "kids",
    title: "Kabouterwandeling",
    date: "16/7 t.e.m. 9/8",
    time: "Dagelijks vanaf 13u",
    description:
      "Ga op pad en vind alle kabouters op het terrein rondom Mechelen Beach.",
    location: "Kinderstrand",
    thumbnail: KABOUTERWANDELING_PHOTO,
  },

  // Kindergrimme & glittertattoos
  {
    cat: "kids",
    title: "Kindergrimme & glittertattoos",
    date: "29/07 & 05/08",
    dates: [
      { startDate: "2026-07-29", endDate: "2026-07-29", startTime: "13:00", endTime: "17:30" },
      { startDate: "2026-08-05", endDate: "2026-08-05", startTime: "13:00", endTime: "17:30" },
    ],
    time: "Woensdagnamiddag vanaf 13u",
    description:
      "Kindergrimme en glittertattoos op de kindernamiddag. Kom je gezicht laten schminken of kies je favoriete glittertattoo!",
    location: "Kinderstrand",
  },

  // Party
  {
    cat: "party",
    title: "DJ Night met DJ Dysfunkshunal",
    date: "17/07",
    time: "Vanaf 19u",
    description: "DJ Night met DJ Dysfunkshunal.",
    location: "Tent",
    thumbnail: DJ_DYSFUNKSHUNAL_PHOTO,
    thumbnailPosition: "top",
  },
  {
    cat: "party",
    title: "Maanrock DJ Rally",
    date: "18/07",
    time: "20u30 – 01u",
    description:
      "DJ Rally in samenwerking met Maanrock. De winnaar krijgt een plekje op Maanrock 2026. Afterparty met de winnaar van 2025 MT DnB.",
    location: "Tent",
    thumbnail: "https://customer-assets-jt897jd0.emergentagent.net/job_mechelen-summer/artifacts/tc89dmpe_maanrock%20dj%20rally%20visual.jpg",
    thumbnailFit: "contain",
    button: {
      label: "Meer info",
      href: "https://vi.be/platform/maanrockrally/calls/MaanrockDjRally2026",
    },
  },
  {
    cat: "party",
    title: "DJ Night met Kurt Verheyen en Shane-D",
    date: "24/07",
    time: "21u – 02u",
    description: "DJ Night met Kurt Verheyen en Shane-D.",
    location: "Tent",
    thumbnails: [
      { src: DJ_KURT_PHOTO, alt: "Kurt Verheyen" },
      { src: DJ_SHANE_D_PHOTO, alt: "Shane-D" },
    ],
  },
  {
    cat: "party",
    title: "Vibrationz",
    date: "25/07",
    time: "20u – 02u",
    description:
      "Party in samenwerking met Vibrationz. Bring Back Those Dayz met Gee, Kayman en Josh Lasden.",
    paragraphs: [
      "Ontdek alles over het explosieve nightlife concept op www.vibrationz.be",
      "Met de baseline \"Bring Back Those Dayz\" brengt VIBRATIONZ party people die de hoogdagen van de Belgische clubscene hebben beleefd samen met nieuwe generaties die ervan willen proeven. Geïnspireerd op nightlife in the old dayz en met respect voor mekaar.",
      "Onze DJ's draaien platen op volle lengte en zetten clubs en festivals in vuur en vlam, zoals ze dat al jaren doen in binnen- en buitenland.",
      "Kom dansen met likeminded people op hun opzwepende mix van club classics en nieuwe sounds in House, Trance en Progressive!",
    ],
    link: { token: "www.vibrationz.be", href: "https://www.vibrationz.be/" },
    location: "Tent",
    visual: VIBRATIONZ_POSTER,
    highlight: true,
  },
  {
    cat: "party",
    title: "DJ Night met DJ Dysfunkshunal",
    date: "31/07",
    time: "Vanaf 20u",
    description: "DJ Night met DJ Dysfunkshunal.",
    location: "Tent",
    thumbnail: DJ_DYSFUNKSHUNAL_PHOTO,
    thumbnailPosition: "top",
  },
  {
    cat: "party",
    title: "Nineties & Nillies Party",
    date: "01/08",
    time: "Vanaf 20u",
    description: "Nineties & Nillies Party met Smashed DJ's & Lobel.",
    location: "Tent",
    thumbnails: [
      { src: DJ_SMASHED_PHOTO, alt: "Smashed DJ's" },
      { src: LOBEL_PHOTO, alt: "Lobel" },
    ],
  },
  {
    cat: "party",
    title: "DJ Night met Kurt Verheyen en Kezze",
    date: "07/08",
    time: "Vanaf 21u",
    description: "DJ Night met Kurt Verheyen en Kezze.",
    location: "Tent",
    thumbnails: [
      { src: DJ_KURT_PHOTO, alt: "Kurt Verheyen" },
      { src: DJ_KEZZE_PHOTO, alt: "Kezze" },
    ],
  },
  {
    cat: "party",
    title: "L'Eau De Vie",
    date: "08/08",
    time: "Vanaf 21u",
    description: "Party in samenwerking met L'Eau De Vie — House music.",
    location: "Tent",
    button: {
      label: "Bekijk op Instagram",
      href: "https://www.instagram.com/leaudevie_official/",
    },
  },

  // Muziek / optredens
  {
    cat: "muziek",
    title: "Akoestisch optreden: Xerxes Trio",
    slug: "xerxes-trio",
    date: "26/07",
    time: "17u – 18u30",
    startDate: "2026-07-26",
    endDate: "2026-07-26",
    startTime: "17:00",
    endTime: "18:30",
    description:
      "Op zondag 26 juli om 17.00 uur verwelkomen we Xerxes Trio, met zanger Xerxes Wouters, bekend van The Voice van Vlaanderen 2024. Samen zorgen zij voor een sfeervolle muzikale namiddag. Kom dat zien!",
    location: "Douaneplein / Transit M",
    highlight: true,
  },
];

const KIDS_DETAILS = [
  {
    date: "21/07",
    title: "Feestdag — opening kinderstrand",
    description: "Grime, crea, boogschieten en knutselworkshop.",
    partners: "Jeti VZW en Speelgoedmuseum",
  },
  {
    date: "22/07",
    title: "Kindernamiddag met extra activiteiten",
    description: "XL-spelletjes.",
    partners: "Speelgoedmuseum",
  },
  {
    date: "26/07",
    title: "Kindernamiddag met extra activiteiten",
    description:
      "Knutselworkshop, circusactiviteiten, schaakinitiatie en ballonnenclown Ivan.",
    partners:
      "Speelgoedmuseum, Cirkolito, Schaakclub Mechelen en ballonnenclown Ivan",
  },
  {
    date: "29/07",
    title: "Kindernamiddag met extra activiteiten",
    description: "XL-spelletjes.",
    partners: "Speelgoedmuseum",
  },
  {
    date: "02/08",
    title: "Kindernamiddag met extra activiteiten",
    description: "Knutselworkshop, schaakinitiatie en ballonnenclown Ivan.",
    partners: "Speelgoedmuseum, Schaakclub Mechelen en ballonnenclown Ivan",
  },
  {
    date: "05/08",
    title: "Kindernamiddag met extra activiteiten",
    description: "XL-spelletjes.",
    partners: "Speelgoedmuseum",
  },
  {
    date: "09/08",
    title: "Kindernamiddag met extra activiteiten",
    description: "Bouwactiviteiten en knutselworkshop.",
    partners: "Jeti VZW en Speelgoedmuseum",
  },
];

function CategoryChip({ cat }) {
  const meta = CAT_META[cat];
  if (!meta) return null;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] ${meta.tone}`}
    >
      {meta.label}
    </span>
  );
}

// ---- Date / status helpers -------------------------------------------------

function parseDMFromString(str) {
  const m = String(str || "").match(/(\d{1,2})\s*\/\s*(\d{1,2})/);
  if (!m) return null;
  return { day: parseInt(m[1], 10), month: parseInt(m[2], 10) };
}

function parseLastDMFromString(str) {
  const matches = [...String(str || "").matchAll(/(\d{1,2})\s*\/\s*(\d{1,2})/g)];
  if (matches.length === 0) return null;
  const last = matches[matches.length - 1];
  return { day: parseInt(last[1], 10), month: parseInt(last[2], 10) };
}

function parseFirstHM(str, fallback = { h: 13, m: 0 }) {
  const s = String(str || "");
  const m = s.match(/(\d{1,2})\s*u\s*(\d{2})?/i);
  if (!m) return fallback;
  return { h: parseInt(m[1], 10), m: m[2] ? parseInt(m[2], 10) : 0 };
}

function getItemDateRange(item) {
  let sY, sM, sD, sH, sMin, eY, eM, eD, eH, eMin;

  if (item.startDate) {
    const [y, m, d] = item.startDate.split("-").map(Number);
    sY = y; sM = m; sD = d;
  } else {
    const dm = parseDMFromString(item.date);
    if (!dm) return null;
    sY = EVENT_YEAR; sM = dm.month; sD = dm.day;
  }

  if (item.endDate) {
    const [y, m, d] = item.endDate.split("-").map(Number);
    eY = y; eM = m; eD = d;
  } else {
    const lastDm = parseLastDMFromString(item.date);
    eY = EVENT_YEAR; eM = lastDm ? lastDm.month : sM; eD = lastDm ? lastDm.day : sD;
  }

  if (item.startTime) {
    const [h, mn] = item.startTime.split(":").map(Number);
    sH = h; sMin = mn;
  } else {
    const t = parseFirstHM(item.time, { h: 13, m: 0 });
    sH = t.h; sMin = t.m;
  }

  if (item.endTime) {
    const [h, mn] = item.endTime.split(":").map(Number);
    eH = h; eMin = mn;
  } else {
    // Only treat as separate end time if the time string actually has ≥ 2 time
    // tokens (e.g. "20u30 – 01u"). Otherwise ("Vanaf 19u") default to 23:59
    // of endDate so the activity flips to "past" the next day.
    const timeStr = String(item.time || "");
    const matches = [...timeStr.matchAll(/(\d{1,2})\s*u\s*(\d{2})?/gi)];
    if (matches.length >= 2) {
      const last = matches[matches.length - 1];
      eH = parseInt(last[1], 10);
      eMin = last[2] ? parseInt(last[2], 10) : 0;
      // overnight: end earlier than start on same day → next day
      if (sY === eY && sM === eM && sD === eD && (eH < sH || (eH === sH && eMin < sMin))) {
        const next = new Date(eY, eM - 1, eD + 1);
        eY = next.getFullYear();
        eM = next.getMonth() + 1;
        eD = next.getDate();
      }
    } else {
      eH = 23; eMin = 59;
    }
  }

  return {
    start: new Date(sY, sM - 1, sD, sH, sMin),
    end: new Date(eY, eM - 1, eD, eH, eMin),
  };
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Returns a Date whose local-time values represent the current wall-clock time
// in Europe/Brussels. This makes status comparisons consistent no matter which
// timezone the user's browser is in.
function nowInBrussels() {
  const now = new Date();
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Brussels",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(now);
    const p = {};
    parts.forEach((x) => {
      if (x.type !== "literal") p[x.type] = x.value;
    });
    return new Date(
      parseInt(p.year, 10),
      parseInt(p.month, 10) - 1,
      parseInt(p.day, 10),
      parseInt(p.hour, 10) % 24,
      parseInt(p.minute, 10),
      parseInt(p.second, 10),
    );
  } catch {
    return now;
  }
}

function getItemStatus(item, now) {
  // Multi-date items: check each date individually.
  if (Array.isArray(item.dates) && item.dates.length > 0) {
    const statuses = item.dates.map((d) => {
      const r = getItemDateRange({ ...d, time: d.time || item.time });
      if (!r) return "upcoming";
      if (now > r.end) return "past";
      const nowDay = startOfDay(now);
      const s = startOfDay(r.start);
      const e = startOfDay(r.end);
      if (nowDay.getTime() >= s.getTime() && nowDay.getTime() <= e.getTime()) return "today";
      return "upcoming";
    });
    if (statuses.includes("today")) return "today";
    if (statuses.every((s) => s === "past")) return "past";
    return "upcoming";
  }

  const range = getItemDateRange(item);
  if (!range) return "upcoming";
  const { start, end } = range;
  if (now > end) return "past";
  const nowDay = startOfDay(now);
  const startDay = startOfDay(start);
  const endDay = startOfDay(end);
  if (nowDay.getTime() >= startDay.getTime() && nowDay.getTime() <= endDay.getTime()) return "today";
  return "upcoming";
}

function getWeekdayNL(date) {
  return NL_WEEKDAYS[date.getDay()];
}

function formatDateLabel(item) {
  const raw = String(item.date || "");
  // Multi-date items: show each date with weekday prefix
  if (Array.isArray(item.dates) && item.dates.length > 0) {
    return item.dates
      .map((d) => {
        const [y, mo, day] = String(d.startDate || "").split("-").map(Number);
        if (!y) return null;
        const dt = new Date(y, mo - 1, day);
        return `${getWeekdayNL(dt)} ${String(day).padStart(2, "0")}/${String(mo).padStart(2, "0")}`;
      })
      .filter(Boolean)
      .join(", ");
  }
  const matches = [...raw.matchAll(/(\d{1,2})\s*\/\s*(\d{1,2})/g)];
  const range = getItemDateRange(item);
  if (matches.length === 1 && range) {
    const wd = getWeekdayNL(range.start);
    const dd = String(range.start.getDate()).padStart(2, "0");
    const mm = String(range.start.getMonth() + 1).padStart(2, "0");
    return `${wd} ${dd}/${mm}`;
  }
  return raw;
}

function slugify(str) {
  return String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
}

function getItemSlug(item) {
  if (item.slug) return item.slug;
  const dm = parseDMFromString(item.date);
  const suffix = dm ? `-${String(dm.day).padStart(2, "0")}-${String(dm.month).padStart(2, "0")}` : "";
  return slugify(item.title) + suffix;
}

function renderWithLink(text, link) {
  if (!link || !link.token) return text;
  const parts = text.split(link.token);
  if (parts.length === 1) return text;
  const out = [];
  parts.forEach((part, i) => {
    out.push(part);
    if (i < parts.length - 1) {
      out.push(
        <a
          key={`lnk-${i}-${link.token}`}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#ed6a1a] hover:text-[#c54508] underline underline-offset-2 font-medium break-all"
        >
          {link.token}
        </a>,
      );
    }
  });
  return out;
}

function parseDateSort(item) {
  const d = String(item.date || "");
  // Find first dd/mm pattern (with or without zero padding)
  const m = d.match(/(\d{1,2})\s*\/\s*(\d{1,2})/);
  if (!m) return { mon: 99, day: 99 };
  return { day: parseInt(m[1], 10), mon: parseInt(m[2], 10) };
}

function parseTimeSort(item) {
  const t = String(item.time || "");
  const m = t.match(/(\d{1,2})\s*u\s*(\d{2})?/i);
  if (!m) return 99;
  const h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  return h + min / 60;
}

function sortChronologically(items) {
  return [...items].sort((a, b) => {
    const da = parseDateSort(a);
    const db = parseDateSort(b);
    if (da.mon !== db.mon) return da.mon - db.mon;
    if (da.day !== db.day) return da.day - db.day;
    return parseTimeSort(a) - parseTimeSort(b);
  });
}

function ShareButton({ slug }) {
  const [copied, setCopied] = useState(false);
  const onShare = useCallback(async (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/programma#${slug}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // fallback: navigate to anchor
        window.location.hash = slug;
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.location.hash = slug;
    }
  }, [slug]);

  return (
    <button
      type="button"
      onClick={onShare}
      data-testid={`programma-share-${slug}`}
      title="Kopieer link naar deze activiteit"
      aria-label="Deel activiteit"
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#8a7460] hover:text-[#ed6a1a] transition-colors"
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5" />
          Link gekopieerd
        </>
      ) : (
        <>
          <Share2 className="h-3.5 w-3.5" />
          Deel activiteit
        </>
      )}
    </button>
  );
}

function StatusBadge({ status }) {
  if (status === "past") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] bg-[#e5ded4] text-[#6b5945]">
        Afgelopen
      </span>
    );
  }
  if (status === "today") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] bg-[#fde3c5] text-[#a8470a] ring-1 ring-[#ed6a1a]/40">
        <span className="h-1.5 w-1.5 rounded-full bg-[#ed6a1a] animate-pulse" />
        Vandaag
      </span>
    );
  }
  return null;
}

function ProgramCard({ item, featured = false, status = "upcoming" }) {
  const isWide = !!item.visual;
  const slug = getItemSlug(item);
  const isPast = status === "past";
  const dateLabel = formatDateLabel(item);

  const renderSingleButton = (btn, idx, isPrimary = false) => {
    const slug = `${item.title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}-${idx}`;
    const baseCls = isPrimary
      ? "inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full bg-orange-gradient text-white shadow-sm hover:opacity-95 transition-opacity"
      : "inline-flex items-center gap-2 text-sm font-semibold text-[#ed6a1a] hover:text-[#c54508] transition-colors";
    if (btn.to) {
      return (
        <Link
          key={idx}
          to={btn.to}
          data-testid={`programma-btn-${slug}`}
          className={baseCls}
        >
          {btn.label}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      );
    }
    return (
      <a
        key={idx}
        href={btn.href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`programma-btn-${slug}`}
        className={baseCls}
      >
        {btn.label}
        <ArrowRight className="h-3.5 w-3.5" />
      </a>
    );
  };

  let Button = null;
  if (item.buttons && item.buttons.length > 0) {
    Button = (
      <div className="mt-5 flex flex-wrap items-center gap-3">
        {item.buttons.map((b, i) => renderSingleButton(b, i, !!b.primary))}
      </div>
    );
  } else if (item.button) {
    Button = <div className="mt-5">{renderSingleButton(item.button, 0)}</div>;
  }

  const Meta = (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <CategoryChip cat={item.cat} />
        {Array.isArray(item.extraCats) &&
          item.extraCats.map((c) => <CategoryChip key={`xc-${c}`} cat={c} />)}
        {featured && !isPast && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] bg-orange-gradient text-white">
            <Sparkles className="h-3 w-3" /> Uitgelicht
          </span>
        )}
        <StatusBadge status={status} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-[#6b5945]">
        <span className="inline-flex items-center gap-1.5">
          <CalendarDays className="h-3.5 w-3.5 text-[#ed6a1a]" />
          {dateLabel}
        </span>
        {item.time && (
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-[#ed6a1a]" />
            {item.time}
          </span>
        )}
      </div>

      <h3 className="font-display mt-2 text-xl sm:text-2xl font-semibold text-[#2b1c10] leading-snug">
        {item.title}
      </h3>

      {Array.isArray(item.paragraphs) && item.paragraphs.length > 0 ? (
        <div className="mt-3 space-y-3 text-sm sm:text-base text-[#6b5945] leading-relaxed">
          {item.paragraphs.map((p, i) => (
            <p key={`para-${i}`}>{renderWithLink(p, item.link)}</p>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-sm sm:text-base text-[#6b5945] leading-relaxed">
          {item.description}
        </p>
      )}

      {item.location && (
        <div className="mt-4 flex items-center gap-1.5 text-xs text-[#8a7460]">
          <MapPin className="h-3.5 w-3.5" />
          {item.location}
        </div>
      )}

      {Button}

      <div className="mt-4 pt-4 border-t border-[#f0e4cf] flex items-center justify-between gap-3">
        <ShareButton slug={slug} />
      </div>
    </>
  );

  const pastClasses = isPast ? "opacity-70 grayscale-[0.25] saturate-75" : "";

  if (isWide) {
    // Featured wide layout: poster left, text right — spans 2 cols on md+
    return (
      <article
        id={slug}
        data-testid={`programma-card-${item.title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}`}
        data-status={status}
        className={`md:col-span-2 lg:col-span-2 group relative rounded-2xl border bg-white overflow-hidden transition-all duration-300 hover:-translate-y-0.5 scroll-mt-40 ${
          isPast
            ? "border-[#d9cdb8] shadow-[0_8px_20px_-16px_rgba(43,28,16,0.2)]"
            : "border-[#f3c79a] shadow-[0_18px_44px_-18px_rgba(237,106,26,0.4)] ring-1 ring-[#f9d8b3]"
        } ${pastClasses}`}
      >
        <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
          <div className={`md:col-span-2 flex items-center justify-center p-4 ${item.visualBg === "white" ? "bg-white border-r border-[#f3c79a]" : "bg-[#0b1722]"}`}>
            <img
              src={item.visual}
              alt={item.title}
              loading="lazy"
              className="w-full max-h-[360px] md:max-h-[420px] object-contain"
            />
          </div>
          <div className="md:col-span-3 p-5 sm:p-6">{Meta}</div>
        </div>
      </article>
    );
  }

  return (
    <article
      id={slug}
      data-testid={`programma-card-${item.title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}`}
      data-status={status}
      className={`group relative rounded-2xl border bg-white overflow-hidden transition-all duration-300 hover:-translate-y-0.5 scroll-mt-40 ${
        isPast
          ? "border-[#d9cdb8] shadow-[0_8px_20px_-16px_rgba(43,28,16,0.2)]"
          : featured
            ? "border-[#f3c79a] shadow-[0_18px_44px_-18px_rgba(237,106,26,0.4)] ring-1 ring-[#f9d8b3]"
            : "border-[#ebd9b5] shadow-[0_10px_28px_-18px_rgba(43,28,16,0.25)] hover:border-[#ed6a1a]"
      } ${pastClasses}`}
    >
      {item.thumbnail && (
        <div
          className={`aspect-[16/9] w-full overflow-hidden ${
            item.thumbnailFit === "contain"
              ? "bg-[#0b1722] flex items-center justify-center"
              : ""
          }`}
        >
          <img
            src={item.thumbnail}
            alt={item.title}
            loading="lazy"
            className={`transition-transform duration-500 group-hover:scale-[1.04] ${
              item.thumbnailFit === "contain"
                ? "max-w-full max-h-full object-contain"
                : "w-full h-full object-cover"
            } ${item.thumbnailPosition === "top" ? "object-[center_20%]" : ""}`}
          />
        </div>
      )}
      {Array.isArray(item.thumbnails) && item.thumbnails.length > 0 && (
        <div className="aspect-[16/9] w-full overflow-hidden bg-[#0b1722] grid grid-cols-2 gap-px">
          {item.thumbnails.map((im) => (
            <div
              key={im.src}
              className="relative overflow-hidden bg-[#0b1722] flex items-center justify-center"
            >
              <img
                src={im.src}
                alt={im.alt}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              />
              <span className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-white/85 text-[#2b1c10]">
                {im.alt}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="p-5 sm:p-6">{Meta}</div>
    </article>
  );
}

export default function Programma() {
  const [active, setActive] = useState("uitgelicht");
  const now = useMemo(() => nowInBrussels(), []);
  const scrolledRef = useRef(false);

  // annotate items with computed status + slug
  const withStatus = useMemo(
    () =>
      PROGRAM.map((p) => ({
        ...p,
        _status: getItemStatus(p, now),
        _slug: getItemSlug(p),
      })),
    [now],
  );

  const sortedAll = useMemo(() => {
    const sorted = sortChronologically(withStatus);
    // move past items to the bottom (still chronological among themselves)
    const active = sorted.filter((p) => p._status !== "past");
    const past = sorted.filter((p) => p._status === "past");
    return [...active, ...past];
  }, [withStatus]);

  const filtered = useMemo(() => {
    if (active === "all") return sortedAll.filter((p) => !p.uitgelichtOnly);
    if (active === "uitgelicht")
      return sortedAll.filter((p) => p.highlight && p._status !== "past");
    if (active === "activiteiten")
      return sortedAll.filter((p) => ACTIVITEITEN_CATS.has(p.cat) && !p.uitgelichtOnly);
    return sortedAll.filter((p) => p.cat === active && !p.uitgelichtOnly);
  }, [active, sortedAll]);

  // Scroll-to-anchor on load or hash change. If the target item isn't visible
  // in the current filter, switch to "all" so the anchor can be shown.
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.replace(/^#/, "");
      if (!hash) return;
      const target = withStatus.find((p) => p._slug === hash);
      if (!target) return;
      const inFilter = filtered.some((p) => p._slug === hash);
      if (!inFilter && !scrolledRef.current) {
        setActive("all");
        return;
      }
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        scrolledRef.current = true;
      }
    };
    // small delay to allow filter render
    const t = setTimeout(scrollToHash, 60);
    window.addEventListener("hashchange", scrollToHash);
    return () => {
      clearTimeout(t);
      window.removeEventListener("hashchange", scrollToHash);
    };
  }, [filtered, withStatus]);

  const activeMeta = CATEGORIES.find((c) => c.id === active);

  return (
    <>
      <Seo
        title="Programma 2026 | Mechelen Beach"
        description="Ontdek het programma van Mechelen Beach 2026 met strandbar, kinderactiviteiten, sport, beachvolleybal, salsa, afterworks en party-avonden op het Douaneplein in Mechelen."
      />

      {/* HERO + FILTERS */}
      <section className="bg-warm-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-20 pb-10 sm:pb-12">
          <span className="eyebrow">Programma 2026</span>
          <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#2b1c10] leading-[1.05] max-w-3xl">
            Wat valt er te beleven?
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#6b5945] max-w-2xl leading-relaxed">
            Ontdek het programma van Mechelen Beach 2026: van strandbar en
            kinderactiviteiten tot sport, beachvolleybal, salsa, afterworks en
            party-avonden.
          </p>
        </div>
      </section>

      {/* CATEGORY FILTERS */}
      <section className="bg-[#fdf8ec] sticky top-24 sm:top-28 z-30 border-y border-[#ebd9b5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div
            className="flex flex-wrap gap-2"
            data-testid="program-filters"
          >
            {CATEGORIES.map((c) => {
              const isUitgelicht = c.id === "uitgelicht";
              const isActive = active === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActive(c.id)}
                  data-testid={`program-filter-${c.id}`}
                  className={`inline-flex items-center gap-1.5 px-3.5 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition-colors ${
                    isActive
                      ? "bg-orange-gradient text-white shadow-md"
                      : isUitgelicht
                        ? "bg-[#fde3c5] text-[#a8470a] border border-[#f3c79a] hover:border-[#ed6a1a]"
                        : "bg-white text-[#2b1c10] border border-[#ebd9b5] hover:border-[#ed6a1a]"
                  }`}
                >
                  {isUitgelicht && <Sparkles className="h-3.5 w-3.5" />}
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROGRAMMA OVERZICHT (gefilterd) */}
      <section data-testid="programma-volledig" className="bg-[#fdf8ec] pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10">
          <div className="mb-6 sm:mb-8">
            <span className="eyebrow">
              {active === "uitgelicht" ? "Niet missen" : "Overzicht"}
            </span>
            <h2 className="font-display mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-[#2b1c10]">
              {active === "all" || active === "uitgelicht"
                ? active === "uitgelicht"
                  ? "Uitgelicht"
                  : "Volledig programma"
                : activeMeta?.label || "Programma"}
            </h2>
            {active !== "all" && (
              <p className="mt-2 text-sm text-[#6b5945]">
                {filtered.length} {filtered.length === 1 ? "activiteit" : "activiteiten"}
                <button
                  type="button"
                  onClick={() => setActive("all")}
                  className="ml-2 font-semibold text-[#ed6a1a] hover:text-[#c54508] underline underline-offset-2"
                  data-testid="program-show-all"
                >
                  Toon volledig programma
                </button>
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {filtered.map((item, i) => (
              <ProgramCard
                key={`p-${item._slug}-${i}`}
                item={item}
                status={item._status}
                featured={(active === "uitgelicht" || item.highlight) && item._status !== "past"}
              />
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-[#6b5945] py-12">
              Geen activiteiten in deze categorie.
            </p>
          )}
        </div>
      </section>

      {/* KINDERACTIVITEITEN IN DETAIL */}
      <section
        data-testid="programma-kinderactiviteiten"
        className="bg-sand-2 py-16 sm:py-20"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="eyebrow">Kinderstrand</span>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl font-semibold tracking-tight text-[#2b1c10]">
              Kinderactiviteiten in detail
            </h2>
            <p className="mt-4 text-base text-[#6b5945] leading-relaxed">
              Tijdens de kindernamiddagen zorgen verschillende partners voor
              extra activiteiten zoals knutselen, XL-spelletjes,
              circusactiviteiten, schaakinitiaties, ballonnen en grime.
            </p>
          </div>

          <div className="mt-10 space-y-4">
            {KIDS_DETAILS.map((k, i) => (
              <article
                key={`k-${i}`}
                data-testid={`kids-detail-${i}`}
                className="rounded-2xl border border-[#ebd9b5] bg-white p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:gap-6"
              >
                <div className="sm:w-28 shrink-0">
                  <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#ed6a1a]">
                    <CalendarDays className="h-4 w-4" />
                    {k.date}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg sm:text-xl font-semibold text-[#2b1c10]">
                    {k.title}
                  </h3>
                  <p className="mt-2 text-sm sm:text-base text-[#6b5945] leading-relaxed">
                    {k.description}
                  </p>
                  <p className="mt-2 text-xs text-[#8a7460]">
                    <span className="font-semibold text-[#2b1c10]">In samenwerking met:</span>{" "}
                    {k.partners}
                  </p>
                </div>
              </article>
            ))}

            <article className="rounded-2xl border border-[#ebd9b5] bg-white p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:gap-6">
              <div className="sm:w-28 shrink-0">
                <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#ed6a1a]">
                  <CalendarDays className="h-4 w-4" />
                  16/7 – 9/8
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-display text-lg sm:text-xl font-semibold text-[#2b1c10]">
                  Kinderstrand
                </h3>
                <p className="mt-1 text-sm text-[#8a7460]">Dagelijks vanaf 13u</p>
                <p className="mt-2 text-sm sm:text-base text-[#6b5945] leading-relaxed">
                  5 springkastelen, kabouterwandeling, foodtrucks en drinks.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ACTIVITEITENPARTNERS */}
      <ActiviteitenPartners />
    </>
  );
}
